import { useUsers } from '../hooks/useUsers'

// Manager picker - shared by EditEventModal and NewEventModal. Renders a
// <select> populated from /api/users (cached for 5 minutes via useUsers).
// Value of `null` means "no manager assigned"; the <option value=""> path
// represents that explicitly so the user can clear an assignment.
//
// Why a select rather than a typeahead/combobox: <50 users for the
// foreseeable future; a native select is faster, accessible by default, and
// works on mobile. We can swap for a richer picker later if the user list
// grows past ~100.

interface Props {
  value: number | null
  onChange: (value: number | null) => void
  label?: string
  disabled?: boolean
}

export function ManagerPicker({ value, onChange, label = 'Manager', disabled }: Props) {
  const { data: users, isLoading, error } = useUsers()

  // Native <select> needs a string value; convert at the boundary.
  const selectValue = value == null ? '' : String(value)
  const handleChange = (raw: string) => {
    onChange(raw === '' ? null : Number(raw))
  }

  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
          {label}
        </span>
        {isLoading && (
          <span className="text-xs text-ink-soft font-mono">loading…</span>
        )}
      </div>
      <select
        value={selectValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || isLoading || !!error}
        className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green focus:ring-1 focus:ring-green/30 transition-colors disabled:opacity-50"
      >
        <option value="">— No manager assigned —</option>
        {users?.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name ?? `(unnamed user #${u.id})`}
            {u.avatarInitials ? ` (${u.avatarInitials})` : ''}
          </option>
        ))}
      </select>
      {error && (
        <div className="text-xs text-oxblood mt-1">
          Could not load users: {(error as Error).message}
        </div>
      )}
    </label>
  )
}
